'use client'

import { cn } from '@/lib/tailwind'
import { ENTRY_PRESENTATION } from '@/entries'
import type { EntryType } from '@/document'

interface ExpandableRowProps {
  /** Whether the row is in expanded state */
  isExpanded: boolean
  /** Whether the row represents a completed/done state (applies muted styling) */
  isDone?: boolean
  /** The header content that's always visible and clickable to expand */
  header: React.ReactNode
  /** The expandable content revealed when expanded */
  children: React.ReactNode
  /** Entry kind, for the left border accent colour. Narrowed from `string` with #390. */
  entryType?: EntryType
  /** Additional className for the container */
  className?: string
}

/**
 * Expandable row component with smooth reveal animation.
 * Used for feed items that can be expanded to show additional details.
 */
export function ExpandableRow({
  isExpanded,
  isDone = false,
  header,
  children,
  entryType,
  className,
}: ExpandableRowProps) {
  const colors = entryType ? ENTRY_PRESENTATION[entryType] : undefined

  return (
    <div
      className={cn(
        'group border-border/30 border-b border-l-2 border-l-transparent transition-colors motion-reduce:transition-none',
        isDone && 'bg-muted/30',
        !isDone && 'hover:bg-muted/40',
        colors && colors.hoverBorder,
        className,
      )}
    >
      {header}

      {/* Expanded content with smooth reveal */}
      <div
        className={cn(
          'grid transition-all duration-200 ease-out motion-reduce:transition-none',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div
          className={cn(
            'overflow-hidden transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
            isExpanded
              ? 'translate-y-0 opacity-100 delay-[50ms]'
              : '-translate-y-1 opacity-0',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

interface RowHeaderProps {
  /** Click handler for expand/collapse */
  onToggle: () => void
  /** Icon component to display */
  icon: React.ReactNode
  /** Primary title text */
  title: string
  /** Whether the row is currently expanded */
  isExpanded?: boolean
  /** Whether the title should have strikethrough styling */
  isStrikethrough?: boolean
  /** Content to show immediately after the title (like topics) */
  titleSuffix?: React.ReactNode
  /** Secondary metadata elements (rendered on the right) */
  metadata?: React.ReactNode
  /** Additional content before the title (like a checkbox button) */
  prefix?: React.ReactNode
  /** Additional className */
  className?: string
}

/**
 * Row header component for feed items.
 * Provides consistent layout for icon, title, and metadata.
 */
export function RowHeader({
  onToggle,
  icon,
  title,
  isExpanded = false,
  isStrikethrough = false,
  titleSuffix,
  metadata,
  prefix,
  className,
}: RowHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-2 sm:gap-3 sm:px-4 sm:py-3',
        className,
      )}
    >
      {prefix}

      <button
        type="button"
        onClick={onToggle}
        aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
        aria-expanded={isExpanded}
        className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:gap-3"
      >
        {/* Icon */}
        <div className="flex h-6 w-6 shrink-0 items-center justify-center sm:h-5 sm:w-5">
          {icon}
        </div>

        {/* Title and metadata */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn(
                'truncate text-sm',
                isStrikethrough && 'text-muted-foreground line-through',
              )}
            >
              {title}
            </span>
            {titleSuffix && <span className="shrink-0">{titleSuffix}</span>}
          </div>
          {metadata && (
            <span className="ml-auto flex shrink-0 items-center gap-2">
              {metadata}
            </span>
          )}
        </div>
      </button>
    </div>
  )
}

interface RowContentProps {
  /** Whether to show poster/image (hidden on mobile) */
  showImage?: boolean
  /** Image element to display */
  image?: React.ReactNode
  /** Main content */
  children: React.ReactNode
}

/**
 * Row expanded content wrapper with optional image.
 * Provides consistent padding and layout for expanded row content.
 */
export function RowContent({
  showImage = false,
  image,
  children,
}: RowContentProps) {
  if (showImage && image) {
    return (
      <div className="flex gap-3 px-2 pt-2 pb-2 sm:gap-4 sm:pr-12 sm:pb-4 sm:pl-12">
        <div className="hidden sm:block">{image}</div>
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
      </div>
    )
  }

  return (
    <div className="px-2 pt-1 pb-2 sm:pr-12 sm:pb-4 sm:pl-12">{children}</div>
  )
}

interface RowActionsProps {
  children: React.ReactNode
  className?: string
}

/**
 * Row actions container (edit/delete buttons).
 * Handles visibility toggle on hover for desktop.
 */
export function RowActions({ children, className }: RowActionsProps) {
  return (
    <div className={cn('flex items-center gap-1', className)}>{children}</div>
  )
}

interface PeopleListProps {
  people: Array<{ id: string; name: string }>
  icon: React.ReactNode
}

/**
 * Inline people list display.
 */
export function PeopleList({ people, icon }: PeopleListProps) {
  if (people.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {people.map((person) => (
        <span
          key={person.id}
          className="text-muted-foreground inline-flex max-w-[200px] items-center gap-1 text-xs"
        >
          <span className="shrink-0">{icon}</span>
          <span className="truncate">{person.name}</span>
        </span>
      ))}
    </div>
  )
}

interface TopicBadgesProps {
  topics?: Array<{ id: string; name: string }> | null
  maxDisplay?: number
}

/**
 * Topic display - shows topics as plain text, comma-separated.
 */
export function TopicBadges({ topics, maxDisplay = 3 }: TopicBadgesProps) {
  if (!topics || topics.length === 0) return null

  const displayTopics = topics.slice(0, maxDisplay)

  return (
    <span className="text-muted-foreground text-xs">
      {displayTopics.map((topic, index) => (
        <span key={topic.id}>
          {topic.name}
          {index < displayTopics.length - 1 && ', '}
        </span>
      ))}
    </span>
  )
}

interface MetaTagsRowProps {
  children: React.ReactNode
  className?: string
}

/**
 * Horizontal flex-wrap container for metadata chips.
 * Renders at the bottom of expanded content to show inline-editable or read-only metadata.
 */
export function MetaTagsRow({ children, className }: MetaTagsRowProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {children}
    </div>
  )
}

interface MetaChipProps {
  icon: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * Read-only metadata chip — subtle pill for displaying metadata values.
 * No hover effect, just display.
 */
export function MetaChip({ icon, children, className }: MetaChipProps) {
  return (
    <span
      className={cn(
        'bg-muted/50 text-muted-foreground inline-flex max-w-[200px] items-center gap-1 rounded-full px-2.5 py-1 text-xs',
        className,
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </span>
  )
}

interface TypeBadgeProps {
  type: string
  /** Entry kind, for the badge colour. Narrowed from `string` with #390. */
  entryType?: EntryType
}

/**
 * Type indicator badge for media entries.
 * Uses entry type identity colors from the centralized color system.
 */
export function TypeBadge({ type, entryType }: TypeBadgeProps) {
  const colors = entryType ? ENTRY_PRESENTATION[entryType] : undefined

  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-medium',
        colors
          ? `${colors.badgeBg} ${colors.badge}`
          : 'bg-muted text-muted-foreground',
      )}
    >
      {type}
    </span>
  )
}
